/**
 * Derived-field formulas. Given the pool length and the ordered lengths (with membership),
 * every lap and session field the watch computes can be reproduced. Verified to match the
 * raw fixtures field-for-field (see IMPLEMENTATION_PLAN.md "Edit operations" and the
 * recompute test). These functions are pure: they return `{ fieldNum: value }` maps; writing
 * them back (and dropping global-113 records) happens elsewhere.
 */
import type { Activity, Length, Stint } from './model'
import { LAP, LEN, SES } from './fields'

/** Garmin rounds half-up: floor(x + 0.5). Not banker's rounding. */
export const R = (x: number): number => Math.floor(x + 0.5)

export type FieldMap = Record<number, number>

const poolMetres = (poolLengthCm: number): number => poolLengthCm / 100

/** Derived fields of one active length. Idle lengths derive nothing here. */
export function recomputeLength(length: Length, poolLengthCm: number): FieldMap {
  if (!length.active) return {}
  const seconds = length.durationMs / 1000
  const poolM = poolMetres(poolLengthCm)
  return {
    [LEN.avgSpeed]: R((poolM / seconds) * 1000),
    [LEN.avgSwimmingCadence]: R((length.strokes / seconds) * 60),
  }
}

interface StintTotals {
  nAct: number
  sumAll: number
  sumAct: number
  strokes: number
  maxSpeed: number
}

/**
 * The stroke shared by every active length, or undefined if there are no active lengths or
 * they disagree. Used to ripple swim_stroke up to the lap (f38) and session (f43) — only when
 * unambiguous, matching Garmin's own behaviour.
 */
export function commonActiveStroke(lengths: readonly Length[]): number | undefined {
  let stroke: number | undefined
  for (const l of lengths) {
    if (!l.active) continue
    if (stroke === undefined) stroke = l.swimStroke
    else if (stroke !== l.swimStroke) return undefined
  }
  return stroke
}

function totals(lengths: readonly Length[]): StintTotals {
  let nAct = 0
  let sumAll = 0
  let sumAct = 0
  let strokes = 0
  let maxSpeed = 0
  for (const l of lengths) {
    sumAll += l.durationMs
    if (l.active) {
      nAct += 1
      sumAct += l.durationMs
      strokes += l.strokes
      if (l.avgSpeed > maxSpeed) maxSpeed = l.avgSpeed
    }
  }
  return { nAct, sumAll, sumAct, strokes, maxSpeed }
}

/** Derived fields of one lap (stint). Rest laps write literal 0 into the speed/stroke fields. */
export function recomputeLap(stint: Stint, poolLengthCm: number): FieldMap {
  const { nAct, sumAll, sumAct, strokes, maxSpeed } = totals(stint.lengths)
  const poolM = poolMetres(poolLengthCm)
  const first = stint.lengths[0]
  const map: FieldMap = {
    [LAP.firstLengthIndex]: first ? first.ordinal : 0,
    [LAP.numLengths]: nAct,
    [LAP.numActiveLengths]: nAct,
    [LAP.totalElapsed]: sumAll,
    [LAP.totalTimer]: sumAll,
    [LAP.totalDistance]: R(nAct * poolM * 100),
    [LAP.totalCycles]: strokes,
    [LAP.startTime]: first ? first.startTime : 0,
    [LAP.enhAvgSpeed]: nAct ? R(((nAct * poolM) / (sumAct / 1000)) * 1000) : 0,
    [LAP.enhMaxSpeed]: nAct ? maxSpeed : 0,
    [LAP.swolf]: nAct ? R(sumAct / 1000 / nAct + strokes / nAct) : 0,
    [LAP.strokesPerLengthX10]: nAct ? R((strokes / nAct) * 10) : 0,
  }
  const stroke = commonActiveStroke(stint.lengths)
  if (stroke !== undefined) map[LAP.swimStroke] = stroke
  return map
}

/** Derived fields of the session, aggregated across every length in the activity. */
export function recomputeSession(activity: Activity): FieldMap {
  const { nAct, sumAll, sumAct, strokes, maxSpeed } = totals(activity.lengths)
  const poolM = poolMetres(activity.poolLengthCm)
  const map: FieldMap = {
    [SES.poolLength]: activity.poolLengthCm,
    [SES.numLengths]: nAct,
    [SES.numActiveLengths]: nAct,
    [SES.totalTimer]: sumAll,
    [SES.totalDistance]: R(nAct * poolM * 100),
    [SES.totalCycles]: strokes,
    [SES.enhAvgSpeed]: R(((nAct * poolM) / (sumAct / 1000)) * 1000),
    [SES.enhMaxSpeed]: maxSpeed,
    [SES.strokesPerLengthX10]: R((strokes / nAct) * 10),
    [SES.swolf]: R(sumAct / 1000 / nAct + strokes / nAct),
  }
  const stroke = commonActiveStroke(activity.lengths)
  if (stroke !== undefined) map[SES.swimStroke] = stroke
  return map
}
